import type { ApiConfigCreateRequest, ApiConfigUpdateRequestBody } from '../../../common/types';
import { extractErrorsFromApi, getAgencyAndMerchantIdFromOwnerEntity } from '../../../common/helpers';
import { Form, Formik, type FormikHelpers } from 'formik';
import type { OwnerEntityType, OwnerType } from '../../../common/constants';
import { useEffect, useState } from 'react';
import { AddApiConfigSchema } from '../sticky-config.schema';
import ApiConfigFormV2 from './ApiConfigForm';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { useAddApiConfigMutation } from '../sticky-config.api-slice';

import { useTranslation } from 'react-i18next';

interface IProps {
  isOpen: boolean;
  toggleModal: () => any;
  ownerEntityType: OwnerType;
  ownerEntity: OwnerEntityType;
}

const initialValue: ApiConfigCreateRequest = {
  userId: '',
  userApiKey: '',
  userPassword: '',
  campaignId: '',
  accountId: ''
};

const AddApiConfig: React.FC<IProps> = ({ isOpen, toggleModal, ownerEntityType, ownerEntity }) => {
  const [addItem, { isLoading, isError, error, isSuccess, reset }] = useAddApiConfigMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleAddItem = async (
    values: ApiConfigUpdateRequestBody,
    action: FormikHelpers<ApiConfigUpdateRequestBody>
  ): Promise<any> => {
    const { agencyId, merchantId } = getAgencyAndMerchantIdFromOwnerEntity(ownerEntityType, ownerEntity);
    await addItem({ ...values, ownerEntityType, ownerEntityId: ownerEntity.id, agencyId, merchantId });
    toggleModal();
  };

  useEffect(() => {
    if (isSuccess) {
      toast(t('apiConfigs.add.successMessage'));
      reset();
    }
    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [isError, isSuccess]);

  return (
    <>
      {isOpen && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">{t('apiConfigs.add.title')}</h3>
                </div>
                <div className="relative p-6 flex-auto w-full">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {
                    <Formik initialValues={initialValue} onSubmit={handleAddItem} validationSchema={AddApiConfigSchema}>
                      {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-start px-2" autoComplete="off">
                          <ApiConfigFormV2 />
                          <div className="w-full flex space-x-2 py-6 mt-4 border-t border-solid border-slate-200 rounded-b">
                            <button className="w-1/2 text-black px-5 py-2 rounded-lg bg-gray-200" onClick={toggleModal}>
                              {t('apiConfigs.add.cancelBtnLabel')}
                            </button>
                            <button
                              type="submit"
                              disabled={isLoading || isSubmitting}
                              className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                            >
                              {t('apiConfigs.add.saveBtnLabel')}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
};

export default AddApiConfig;
